import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT, useI18n } from "@/context/I18nContext";
import AuthLayout from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { localizeErr } from "@/lib/errors";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

/** Google 'G' logo SVG — official multi-colour mark. */
function GoogleGlyph({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

/** Start the Emergent-managed Google Auth flow.
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href =
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const t = useT();
  const { lang } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const nextTo = sp.get("next") || loc.state?.from || "/app";
  const audience = sp.get("audience") === "business" ? "business" : "personal";
  const isBusiness = audience === "business";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login({ email: email.trim(), password });
      toast.success(t("auth.welcomeBack"));
      nav(nextTo, { replace: true });
    } catch (e) {
      const msg = localizeErr(e, lang, t("errors.generic"));
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      eyebrow={isBusiness ? t("b2b.auth.businessEyebrow") : t("b2b.auth.personalEyebrow")}
      title={isBusiness ? t("b2b.auth.businessTitle") : t("auth.welcomeBack")}
      subtitle={isBusiness ? t("b2b.auth.businessSub") : t("auth.welcomeSub")}
      footer={
        <div className="space-y-2">
          <div>
            {t("auth.newHere")}{" "}
            <Link to={isBusiness ? "/register?audience=business" : "/register"} data-testid="login-goto-register" className="text-sage-600 font-semibold hover:underline">
              {t("auth.signUp")}
            </Link>
          </div>
          <div>
            <Link to="/" data-testid="login-switch-audience" className="text-xs text-ink-500 hover:underline">{t("b2b.auth.switchAudience")}</Link>
          </div>
        </div>
      }
    >
      <div className="space-y-5" data-testid="login-form-wrap">
        {/* Continue with Google — placed above the email/password form as requested */}
        <button
          type="button"
          onClick={startGoogleAuth}
          data-testid="login-google"
          className="w-full h-11 rounded-full bg-white border border-border shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition inline-flex items-center justify-center gap-2.5 text-ink-900 font-medium"
        >
          <GoogleGlyph className="h-5 w-5" />
          {t("auth.continueWithGoogle")}
        </button>

        <div className="flex items-center gap-3 my-1" data-testid="login-divider">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-ink-500">{t("auth.orContinueWith")}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-ink-700">{t("common.email")}</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 h-11 rounded-xl bg-white"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-ink-700">{t("common.password")}</Label>
              <Link to="/forgot-password" data-testid="login-forgot" className="text-xs text-sage-600 hover:underline">
                {t("auth.forgot")}
              </Link>
            </div>
            <div className="relative mt-1.5">
              <Input
                id="password"
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 rounded-xl bg-white pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                data-testid="login-toggle-password"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {err && (
            <div data-testid="login-error" className="rounded-xl bg-rust-500/10 border border-rust-500/20 text-rust-600 text-sm px-3 py-2">
              {err}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="w-full h-11 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium"
          >
            {busy ? t("common.loading") : t("auth.signIn")}
          </Button>

          {isBusiness && (
            <div className="pt-2 text-center text-sm text-ink-500" data-testid="login-business-help">
              <Link to="/accept-invite" data-testid="login-accept-invite" className="text-sage-600 font-semibold hover:underline">
                {t("auth.acceptInvite")}
              </Link>
              <div className="mt-1 text-xs">
                {t("b2b.auth.orgOnboard")}{" "}
                <a href="mailto:contact@innercompass.tech" className="text-sage-600 hover:underline">contact@innercompass.tech</a>
              </div>
            </div>
          )}
        </form>
      </div>
    </AuthLayout>
  );
}
